import { getAdminClients, getBusinessCenterProjects } from "../../lib/api";
import { loadARRecoveryWorkspace } from "../../lib/arRecoveryData";
import { createUniversalOperationalRecord, type UniversalOperationalRecord } from "../../lib/universalOperationalEngines";
import { loadBusinessCenterDocuments } from "./businessCenterDocumentsData";
import { loadBusinessCenterTasks } from "./businessCenterTasksData";

export const validationRecord = {
  workspace: "business-center-os" as const,
  recordType: "project" as const,
  recordId: "bc-project-validation-001",
  title: "Business Center OS Project Validation Record",
  owner: "Operations Admin",
};

export const validationRegistryRecord = createUniversalOperationalRecord({
  id: validationRecord.recordId,
  workspace: validationRecord.workspace,
  recordType: validationRecord.recordType,
  recordName: validationRecord.title,
  status: "active",
  owner: validationRecord.owner,
  route: "/admin/truevance-project-center",
  source: "Phase 13 validation record",
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export async function buildSeedRegistryRecords(): Promise<UniversalOperationalRecord[]> {
  const now = new Date().toISOString();
  const records: UniversalOperationalRecord[] = [validationRegistryRecord];

  try {
    const clients = await getAdminClients();
    clients.forEach((client) => {
      records.push(
        createUniversalOperationalRecord({
          id: client.id,
          workspace: client.brand === "kreatethedate" ? "kreatethedate-os" : "business-center-os",
          recordType: "client",
          recordName: client.clientName || client.organizationName || client.contactName || client.id,
          status: client.clientStatus || client.storageSetupStatus || "active",
          owner: client.contactName || client.contactEmail || client.clientEmail,
          route: "/admin/truevance-client-center",
          source: "Client Center",
          createdAt: client.createdAt || now,
          updatedAt: client.updatedAt || client.lastActivityAt || now,
        }),
      );
    });
  } catch {
    // Client records remain optional in local-only previews.
  }

  try {
    const workspace = await getBusinessCenterProjects();
    workspace.projects.forEach((project) => {
      records.push(
        createUniversalOperationalRecord({
          id: project.id,
          workspace: "business-center-os",
          recordType: "project",
          recordName: project.projectName || project.projectNumber || project.id,
          status: project.projectStatus,
          owner: project.assignedTeam || project.clientName,
          relatedClientId: project.clientId || undefined,
          route: `/admin/truevance-project-center/${project.id}`,
          source: "Project Center",
          createdAt: project.createdAt || now,
          updatedAt: project.updatedAt || project.lastActivityAt || now,
        }),
      );
    });
  } catch {
    // Project records remain optional in local-only previews.
  }

  loadBusinessCenterTasks().forEach((task) => {
    records.push(
      createUniversalOperationalRecord({
        id: task.id,
        workspace: "business-center-os",
        recordType: "task",
        recordName: task.taskTitle || task.taskNumber || task.id,
        status: task.status,
        owner: task.assignedMember,
        relatedClientId: task.clientId || undefined,
        relatedProjectId: task.projectId || undefined,
        route: "/admin/truevance-tasks",
        source: "Work Queue task module",
        createdAt: task.createdAt || now,
        updatedAt: task.updatedAt || now,
      }),
    );
  });

  loadBusinessCenterDocuments().forEach((document) => {
    records.push(
      createUniversalOperationalRecord({
        id: document.id,
        workspace: "business-center-os",
        recordType: "document",
        recordName: document.documentName || document.documentNumber || document.id,
        status: document.status,
        owner: document.clientName || document.projectName,
        relatedClientId: document.clientId || undefined,
        relatedProjectId: document.projectId || undefined,
        route: "/admin/truevance-document-center",
        source: "Document Center",
        createdAt: document.createdAt || now,
        updatedAt: document.updatedAt || now,
      }),
    );
  });

  const arWorkspace = loadARRecoveryWorkspace();
  arWorkspace.accounts.forEach((account) => {
    records.push(
      createUniversalOperationalRecord({
        id: account.id,
        workspace: "ar-recovery-os",
        recordType: "ar_account",
        recordName: account.customerName || account.customerNumber || account.id,
        status: account.recoveryStatus,
        owner: account.assignedCollector,
        relatedClientId: account.companyId,
        route: "/admin/ar-recovery/customer-accounts",
        source: "AR Recovery Customer Accounts",
        createdAt: account.createdAt || now,
        updatedAt: account.updatedAt || now,
      }),
    );
  });

  arWorkspace.promises.forEach((promise) => {
    records.push(
      createUniversalOperationalRecord({
        id: promise.id,
        workspace: "ar-recovery-os",
        recordType: "ar_promise",
        recordName: `${promise.customerName} promise ${promise.promiseDate}`,
        status: promise.status,
        owner: promise.collector,
        relatedClientId: promise.companyId,
        relatedProjectId: promise.accountId,
        route: "/admin/ar-recovery/promise-to-pay",
        source: "AR Recovery Promise Center",
        createdAt: promise.createdAt || now,
        updatedAt: promise.updatedAt || now,
      }),
    );
  });

  return dedupeRecords(records);
}

function dedupeRecords(records: UniversalOperationalRecord[]) {
  const byKey = new Map<string, UniversalOperationalRecord>();
  records.forEach((record) => {
    byKey.set(`${record.workspace}:${record.recordType}:${record.id}`, record);
  });
  return Array.from(byKey.values());
}
